import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Template, TemplateFormData, TemplatePreview } from '../types';
import { templateService } from '../services/api';
import './TemplateEditor.css';

interface TemplateEditorProps {
  mode: 'create' | 'edit';
}

const TemplateEditor: React.FC<TemplateEditorProps> = ({ mode }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState<TemplateFormData>({
    name: '',
    type: 'email',
    category: 'confirmation',
    subject: '',
    content: '',
  });
  
  const [preview, setPreview] = useState<TemplatePreview | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  
  useEffect(() => {
    if (mode === 'edit' && id) {
      const fetchTemplate = async () => {
        try {
          setLoading(true);
          const template = await templateService.getById(parseInt(id));
          setFormData({
            name: template.name,
            type: template.type,
            category: template.category,
            subject: template.subject || '',
            content: template.content,
          });
          setError(null);
        } catch (err) {
          console.error('Erreur lors de la récupération du modèle:', err);
          setError('Erreur lors de la récupération du modèle. Veuillez réessayer.');
        } finally {
          setLoading(false);
        }
      };
      
      fetchTemplate();
    }
  }, [id, mode]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Réinitialiser le sujet si le type est changé à SMS
    if (name === 'type' && value === 'sms') {
      setFormData(prev => ({
        ...prev,
        subject: '',
      }));
    }
  };
  
  const handlePreview = async () => {
    try {
      setLoading(true);
      const previewData = await templateService.preview(formData);
      setPreview(previewData);
      setShowPreview(true);
      setError(null);
    } catch (err) {
      console.error('Erreur lors de la prévisualisation du modèle:', err);
      setError('Erreur lors de la prévisualisation du modèle. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      if (mode === 'create') {
        await templateService.create(formData);
      } else if (mode === 'edit' && id) {
        await templateService.update(parseInt(id), formData);
      }
      
      navigate('/templates');
    } catch (err) {
      console.error('Erreur lors de l\'enregistrement du modèle:', err);
      setError('Erreur lors de l\'enregistrement du modèle. Veuillez réessayer.');
      setLoading(false);
    }
  };
  
  const getCategoryLabel = (category: string): string => {
    switch (category) {
      case 'confirmation':
        return 'Confirmation';
      case 'reminder':
        return 'Rappel';
      case 'cancellation':
        return 'Annulation';
      case 'other':
        return 'Autre';
      default:
        return category;
    }
  };
  
  if (loading && mode === 'edit') {
    return <div className="loading">Chargement du modèle...</div>;
  }
  
  return (
    <div className="template-editor-container">
      <div className="template-editor-header">
        <h1>{mode === 'create' ? 'Créer un nouveau modèle' : 'Modifier le modèle'}</h1>
        <button 
          className="btn-secondary"
          onClick={() => navigate('/templates')}
        >
          Retour à la liste
        </button>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="template-editor-content">
        <div className="template-form-container">
          <form onSubmit={handleSubmit} className="template-form">
            <div className="form-group">
              <label htmlFor="name">Nom du modèle *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Ex: Confirmation Hospitalisation Complète"
              />
              <div className="form-help">
                <p>Conseil: Incluez le type de rendez-vous dans le nom du modèle pour faciliter la sélection automatique.</p>
                <p>Exemples: "Confirmation Hospitalisation Complète", "Rappel Hospitalisation de Jour"</p>
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="type">Type de communication *</label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                required
              >
                <option value="email">Email</option>
                <option value="sms">SMS</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="category">Catégorie *</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
              >
                <option value="confirmation">Confirmation</option>
                <option value="reminder">Rappel</option>
                <option value="cancellation">Annulation</option>
                <option value="other">Autre</option>
              </select>
            </div>
            
            {formData.type === 'email' && (
              <div className="form-group">
                <label htmlFor="subject">Sujet *</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  placeholder="Ex: Confirmation de votre rendez-vous"
                />
              </div>
            )}
            
            <div className="form-group">
              <label htmlFor="content">Contenu *</label>
              <div className="content-help">
                <p>Utilisez les balises suivantes pour insérer des données dynamiques :</p>
                <ul>
                  <li><code>{"{{civilite}}"}</code> - Civilité du patient (Monsieur/Madame)</li>
                  <li><code>{"{{nom_patient}}"}</code> - Prénom du patient</li>
                  <li><code>{"{{nom_complet}}"}</code> - Prénom et nom du patient</li>
                  <li><code>{"{{date_rdv}}"}</code> - Date du rendez-vous (format long)</li>
                  <li><code>{"{{heure_rdv}}"}</code> - Heure du rendez-vous</li>
                  <li><code>{"{{type}}"}</code> - Type de rendez-vous</li>
                  <li><code>{"{{date_courte}}"}</code> - Date du rendez-vous (format court)</li>
                </ul>
              </div>
              <textarea
                id="content"
                name="content"
                value={formData.content}
                onChange={handleChange}
                required
                rows={10}
                placeholder={formData.type === 'email' 
                  ? "<div style=\"font-family: Arial, sans-serif;\">\n  <h2>Bonjour {{civilite}} {{nom_patient}},</h2>\n  <p>Votre rendez-vous {{type}} est confirmé pour le {{date_rdv}} à {{heure_rdv}}.</p>\n  <p>Cordialement,</p>\n  <p>L'équipe Les Grands Prés</p>\n</div>"
                  : "Bonjour {{civilite}} {{nom_patient}}, votre rendez-vous {{type}} est prévu le {{date_courte}} à {{heure_rdv}}. Merci de vous présenter 15 min avant. L'équipe Les Grands Prés."
                }
              />
            </div>
            
            <div className="form-actions">
              <button 
                type="button" 
                className="btn-secondary"
                onClick={handlePreview}
                disabled={loading}
              >
                Prévisualiser
              </button>
              <button 
                type="submit" 
                className="btn-primary"
                disabled={loading}
              >
                {loading ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </form>
        </div>
        
        {showPreview && preview && (
          <div className="template-preview-container">
            <div className="preview-header">
              <h2>Prévisualisation</h2>
              <button 
                className="btn-close"
                onClick={() => setShowPreview(false)}
              >
                ×
              </button>
            </div>
            
            <div className="preview-content">
              {formData.type === 'email' && (
                <>
                  <div className="preview-subject">
                    <strong>Sujet:</strong> {preview.subject}
                  </div>
                  <div className="preview-divider"></div>
                </>
              )}
              
              <div className={`preview-body ${formData.type === 'email' ? 'email-preview' : 'sms-preview'}`}>
                {formData.type === 'email' ? (
                  <div dangerouslySetInnerHTML={{ __html: preview.content }} />
                ) : (
                  <div className="sms-content">{preview.content}</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateEditor;
